/**
 * Invoice360Page — Vista 360 de la factura de proveedor (Fase 2, read-only).
 *
 * Reusa el shell genérico (@/components/three60): Workspace360 + MoneyTotals +
 * RelatedTabsRail. Centro conmutable Detalle / PDF Clobi / XML; panel contextual
 * de 7 tabs lazy alimentado por los endpoints de agregación de compras.
 * Detrás del flag ui_purchases_360 (convive con el modal de detalle actual).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  User, Activity, Link2, BookOpen, DollarSign, Paperclip, History,
  FileText, Loader2, ChevronLeft, ArrowLeft, ExternalLink, Link as LinkIcon,
  FilePenLine, FileCode2, Download, BadgeCheck, XCircle, Trash2, Plus, Mail, Upload, Pencil, Clock, PackageCheck,
} from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import apiClient from '@/lib/apiClient'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { useQueryClient } from '@tanstack/react-query'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { ApproveWithAccountModal } from '@/modules/accounting/components/ApproveWithAccountModal'
import { VoucherForm } from '@/modules/accounting/components/VoucherForm'
import { useAcuseReciboDTE, useAcceptDTE, useRejectDTE } from '@/modules/dte-inbound/hooks/useInboundDTE'
import { Workspace360, MoneyTotals, buildDocMoneyStats, useClpFormatter, RelatedTabsRail } from '@/components/three60'
import { useLocalizationConfig, useTenantTax } from '@/modules/config/hooks/useConfig'
import {
  useSupplierInvoice, useSupplierInvoices, useSupplierInvoiceAttachments,
  useApproveSupplierInvoice, useCancelSupplierInvoice, useDeleteSupplierInvoice,
  useCreateSupplierPayment, useRequestSupplierXml, useUpdateSupplierInvoice,
  useUpdateInvoiceNotes, useUploadSupplierInvoiceOriginalPdf, useDeleteSupplierInvoiceAttachment,
  useUpdateInvoiceReferencias, nonBillableAmount, payableTotal,
  type SupplierInvoice, type SupplierInvoiceListItem, type POLine, type PayableInvoice,
} from '../../hooks/usePurchases'
import { SupplierPaymentModal } from '../SupplierPaymentModal'
import { EditIntlInvoiceModal } from '../EditIntlInvoiceModal'
import {
  useInvoiceRelated, useInvoiceVoucher, useInvoicePayments, useInvoiceReception, useSupplierContext,
} from './use360PurchaseQueries'

// ─── Helpers de presentación ───────────────────────────────────────────────────

const DTE_LABELS: Record<number, string> = {
  33: 'Factura', 34: 'Factura Exenta', 39: 'Boleta', 46: 'Factura de Compra',
  52: 'Guía de Despacho', 56: 'Nota de Débito', 61: 'Nota de Crédito',
  110: 'Factura Exportación', 111: 'Nota Débito Export.', 112: 'Nota Crédito Export.',
}

const STATUS_CLS: Record<string, string> = {
  rcv_pending: 'bg-warning-surface text-warning-content',
  rcv_reclamado: 'bg-danger-surface text-danger-content',
  registered: 'bg-surface-muted text-content-muted',
  approved: 'bg-success-surface text-success-content',
  paid: 'bg-success-surface text-success-content',
  cancelled: 'bg-surface-muted text-content-subtle line-through',
}

async function openBlob(url: string) {
  const res = await apiClient.get(url, { responseType: 'blob' })
  window.open(URL.createObjectURL(res.data), '_blank', 'noopener')
}

const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manual', xml_import: 'XML', rcv: 'RCV SII', intl_invoice: 'Internacional', honorarios: 'Honorarios',
}

// ─── Página principal ───────────────────────────────────────────────────────────

type CenterView = 'detail' | 'pdf' | 'xml'

export function Invoice360Page() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const locale = useTenantLocale()
  const fmt = useClpFormatter()
  const toast = useToast()

  const { data: locConfig, isLoading: cfgLoading } = useLocalizationConfig()
  const tax = useTenantTax()
  const { data: invoice, isLoading } = useSupplierInvoice(id || null, { keepPrevious: true })

  const [view, setView] = useState<CenterView>('detail')
  const [activeTab, setActiveTab] = useState('supplier')
  const [railSize, setRailSize] = useState(25)

  // Gate del flag: sin ui_purchases_360 → volver al listado clásico.
  useEffect(() => {
    if (!cfgLoading && locConfig && !locConfig.ui_purchases_360) {
      navigate('/purchases/invoices', { replace: true })
    }
  }, [cfgLoading, locConfig, navigate])

  // El rail reusa los filtros del listado (persistidos por SupplierInvoicesPage en
  // sessionStorage) para mostrar el MISMO conjunto que el usuario venía viendo
  // —p. ej. tras buscar un proveedor—, no todas las facturas recientes. Fallback
  // a '-created_at' si se entra directo por deep-link (sin pasar por el listado).
  const railParams = useMemo<Record<string, string>>(() => {
    try {
      const raw = sessionStorage.getItem('purchases-invoices:rail-params')
      const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : null
      return parsed && Object.keys(parsed).length ? parsed : { ordering: '-created_at' }
    } catch {
      return { ordering: '-created_at' }
    }
  }, [])
  const { data: railData, isFetching: railLoading } = useSupplierInvoices({ ...railParams, page_size: railSize })
  const railInvoices = railData?.invoices ?? []
  const railTotal = railData?.count ?? 0

  // ── Fase 3: acciones contextuales (reusan los hooks/modales de la lista) ──
  const { can } = usePermission()
  const qc = useQueryClient()
  const approve = useApproveSupplierInvoice()
  const cancelInvoice = useCancelSupplierInvoice()
  const deleteInvoice = useDeleteSupplierInvoice()
  const createPayment = useCreateSupplierPayment()
  const requestXml = useRequestSupplierXml(id)
  const updateInvoice = useUpdateSupplierInvoice(id)
  const acuseSii = useAcuseReciboDTE()
  const acceptSii = useAcceptDTE()
  const rejectSii = useRejectDTE()
  const { confirm: confirmCancel, modalProps: cancelModalProps } = useConfirmModal()
  const { confirm: confirmDelete, modalProps: deleteModalProps } = useConfirmModal()
  const { confirm: confirmAcuse, modalProps: acuseModalProps } = useConfirmModal()
  const [approveOpen, setApproveOpen] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const [editIntlOpen, setEditIntlOpen] = useState(false)

  if (isLoading || !invoice) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-content-subtle">
        <Loader2 size={22} className="animate-spin mr-2" /> Cargando…
      </div>
    )
  }

  const docLabel = invoice.document_type_sii ? (DTE_LABELS[invoice.document_type_sii] ?? `DTE ${invoice.document_type_sii}`) : 'Factura'

  const canApprove = can('purchases.invoices', 'approve')
  const canDelete = can('purchases.invoices', 'delete')
  const canAcuseSii = can('dte_inbound', 'approve')
  const canPay = can('purchases.payments', 'create')
  const canEditIntl = invoice.source === 'intl_invoice' && can('purchases.invoices', 'edit')
  // Aprobar disponible para 'registered' y —si el tenant lo habilita— para los
  // estados RCV sin XML (contabiliza con los montos del RCV). Mismo gating que el
  // listado (allow_approve_without_xml).
  const allowApproveWithoutXml = !!locConfig?.allow_approve_without_xml
  const canApproveNow = canApprove && (
    invoice.status === 'registered' ||
    (['rcv_pending', 'rcv_reclamado'].includes(invoice.status) && allowApproveWithoutXml)
  )
  const label = `N° ${invoice.invoice_number} de ${invoice.supplier_name}`
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['purchases'] })
    qc.invalidateQueries({ queryKey: ['inv360'] })
  }
  // 409 VOUCHER_APPROVED = asiento firme sin reversa. Ofrecemos generar la
  // reversión y completar la acción, en vez de mandar a "eliminar el comprobante"
  // (imposible: un comprobante aprobado solo se revierte).
  const voucherBlocked = (e: unknown) => {
    const err = e as {
      response?: { data?: { error?: { code?: string; message?: string } } }
    }
    const error = err.response?.data?.error
    return error?.code === 'VOUCHER_APPROVED' ? (error.message ?? '') : null
  }
  const offerReversal = (accion: 'anular' | 'eliminar', backendMsg: string) => confirmCancel({
    message: `${backendMsg}\n\n¿Generar el asiento de reversión y ${accion} la factura ${label}? La reversión deja el asiento sin efecto (invierte DEBE↔HABER) y queda registrada en el Libro Diario.`,
    variant: 'danger', confirmLabel: `Revertir el comprobante y ${accion}`,
    onConfirm: () => new Promise<void>((resolve) => {
      const done = accion === 'anular'
        ? () => { refresh(); resolve() }
        : () => { navigate('/purchases/invoices'); resolve() }
      const mutation = accion === 'anular' ? cancelInvoice : deleteInvoice
      mutation.mutate({ id: invoice.id, reverseVoucher: true }, {
        onSuccess: done,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          toast.error(err.response?.data?.error?.message ?? `No se pudo ${accion} la factura.`)
          resolve()
        },
      })
    }),
  })
  const requestCancel = () => confirmCancel({
    message: `¿Anular la factura ${label}? Esta acción no se puede deshacer.`,
    variant: 'danger', confirmLabel: 'Anular factura',
    onConfirm: () => new Promise<void>((resolve) => {
      cancelInvoice.mutate({ id: invoice.id }, {
        onSuccess: () => { refresh(); resolve() },
        onError: (e: unknown) => {
          const msg = voucherBlocked(e)
          if (msg) offerReversal('anular', msg)
          else {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            toast.error(err.response?.data?.error?.message ?? 'No se pudo anular la factura.')
          }
          resolve()
        },
      })
    }),
  })
  const requestDelete = () => confirmDelete({
    message: `¿Eliminar definitivamente la factura ${label}? Esta acción no se puede deshacer. Si proviene de un DTE recibido, este vuelve a quedar pendiente por importar.`,
    variant: 'danger', confirmLabel: 'Eliminar factura',
    onConfirm: () => new Promise<void>((resolve) => {
      deleteInvoice.mutate({ id: invoice.id }, {
        onSuccess: () => { navigate('/purchases/invoices'); resolve() },
        onError: (e: unknown) => {
          const msg = voucherBlocked(e)
          if (msg) offerReversal('eliminar', msg)
          else {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            toast.error(err.response?.data?.error?.message ?? 'No se pudo eliminar la factura.')
          }
          resolve()
        },
      })
    }),
  })
  const requestAcuseSii = (inboundDteId: string) => confirmAcuse({
    message: `¿Otorgar el acuse de recibo de mercaderías (ERM) al SII por la factura ${label}? Despeja la pestaña "Pendientes" del RCV y da mérito ejecutivo (Ley 19.983).`,
    confirmLabel: 'Acuse de recibo (ERM)',
    onConfirm: () => new Promise<void>((resolve) => {
      acuseSii.mutate({ id: inboundDteId }, { onSuccess: () => { refresh(); resolve() }, onError: () => resolve() })
    }),
  })
  const requestAcceptSii = (inboundDteId: string) => confirmAcuse({
    message: `¿Aceptar el contenido (ACD) ante el SII de la factura ${label}? (Ley 19.983)`,
    confirmLabel: 'Aceptar contenido (ACD)',
    onConfirm: () => new Promise<void>((resolve) => {
      acceptSii.mutate({ id: inboundDteId }, { onSuccess: () => { refresh(); resolve() }, onError: () => resolve() })
    }),
  })
  const requestRejectSii = (inboundDteId: string) => confirmAcuse({
    message: `¿Reclamar (rechazar) ante el SII la factura ${label}? Si ya fue importada, se anulará y se revertirá su comprobante. No se puede deshacer.`,
    variant: 'danger', confirmLabel: 'Reclamar (reclamo SII)',
    onConfirm: () => new Promise<void>((resolve) => {
      rejectSii.mutate({ id: inboundDteId }, { onSuccess: () => { refresh(); resolve() }, onError: () => resolve() })
    }),
  })
  const handleApprove = (accountId: string | undefined) => {
    approve.mutate({ id: invoice.id, accountId }, { onSuccess: () => { setApproveOpen(false); refresh() } })
  }
  const handleSavePayment = (formData: Record<string, unknown>) => {
    createPayment.mutate(formData as Parameters<typeof createPayment.mutate>[0], {
      onSuccess: (data) => {
        setPayOpen(false); refresh()
        if (data?.voucher_draft_warning) {
          toast.warning('Pago registrado. El comprobante quedó en borrador: revisar la cuenta contable (documento con monto no facturable).')
        }
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        toast.error(err.response?.data?.error?.message ?? 'No se pudo registrar el pago.')
      },
    })
  }
  const handleSaveIntl = (data: Record<string, unknown>) => {
    updateInvoice.mutate(data as Parameters<typeof updateInvoice.mutate>[0], {
      onSuccess: () => { setEditIntlOpen(false); refresh(); toast.success('Factura actualizada') },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        toast.error(err.response?.data?.error?.message ?? 'No se pudo guardar la factura.')
      },
    })
  }
  // #71: solicitar XML al proveedor — para facturas conocidas por RCV sin XML.
  const canRequestXml = invoice.status === 'rcv_pending' || (invoice.source === 'rcv' && !invoice.has_xml)
  const onRequestXml = (email: string) => requestXml.mutate(email, {
    onSuccess: (r) => toast.success(`Solicitud enviada a ${r.sent_to}`),
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'No se pudo enviar la solicitud.')
    },
  })

  // Objeto de acciones para los tabs (acuse en Recepción, pago en Pagos).
  const tabActions: TabActions = {
    canAcuseSii, canPay, canRequestXml,
    requestingXml: requestXml.isPending,
    onAcuseSii: requestAcuseSii,
    onAcceptSii: requestAcceptSii,
    onRejectSii: requestRejectSii,
    onRegisterPayment: () => setPayOpen(true),
    onRequestXml,
  }

  const stats = buildDocMoneyStats(
    { subtotal: invoice.subtotal, tax_amount: invoice.tax_amount, total: invoice.total, outstanding_amount: invoice.outstanding },
    fmt,
  )
  if (invoice.currency_original && invoice.total_original) {
    stats.splice(stats.length - 1, 0, {
      label: `Total ${invoice.currency_original}`, variant: 'fx',
      value: `${invoice.currency_original} ${parseFloat(invoice.total_original).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    })
  }
  // Monto no facturable: solo cuando el total facturable es 0 y el monto real va
  // en MontoNF/MontoPeriodo (corredoras, servicios periódicos). Se destaca como
  // principal y se degrada el Total $0 a secundario para no confundir.
  const headerNonBillable = parseFloat(invoice.total) === 0 ? nonBillableAmount(invoice.sii_montos) : null
  if (headerNonBillable) {
    const mainIdx = stats.findIndex((s) => s.variant === 'main')
    if (mainIdx >= 0) stats[mainIdx] = { ...stats[mainIdx], variant: 'default' }
    stats.push({ label: 'No facturable', value: fmt(headerNonBillable), variant: 'main' })
  }
  // Total tributario > 0 con monto no facturable (telecom: VlrPagar = afecto +
  // equipos en cuotas): destacar el Valor a Pagar y degradar el Total afecto.
  const headerPayable = parseFloat(invoice.total) > 0 ? payableTotal(invoice.sii_montos, invoice.total) : null
  if (headerPayable) {
    const mainIdx = stats.findIndex((s) => s.variant === 'main')
    if (mainIdx >= 0) stats[mainIdx] = { ...stats[mainIdx], variant: 'default' }
    stats.push({ label: 'Valor a pagar', value: fmt(headerPayable), variant: 'main' })
  }

  const tabs = [
    { key: 'supplier', icon: <User size={18} />, title: 'Proveedor y origen' },
    { key: 'reception', icon: <Activity size={18} />, title: 'Recepción y SII' },
    { key: 'related', icon: <Link2 size={18} />, title: 'Relacionados', count: invoice.adjustments?.length },
    { key: 'voucher', icon: <BookOpen size={18} />, title: 'Comprobante contable' },
    { key: 'payments', icon: <DollarSign size={18} />, title: 'Pagos' },
    { key: 'files', icon: <Paperclip size={18} />, title: 'Adjuntos' },
    { key: 'history', icon: <History size={18} />, title: 'Historial' },
  ]

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/purchases/invoices/${invoice.id}/360`)
    toast.success('Enlace copiado')
  }

  return (
    <>
    <Workspace360
      rail={
        <DocsRail
          invoices={railInvoices} total={railTotal} activeId={invoice.id} locale={locale} fmt={fmt}
          loading={railLoading}
          onSelect={(iid) => navigate(`/purchases/invoices/${iid}/360`)}
          onLoadMore={() => setRailSize((s) => s + 25)}
          onBack={() => navigate('/purchases/invoices')}
        />
      }
      header={
        <div className="px-[18px] py-3.5">
          <div className="flex items-start gap-3.5">
            <button onClick={() => navigate('/purchases/invoices')} title="Volver al listado"
              className="w-9 h-9 rounded-lg border border-divider grid place-items-center text-content-muted hover:bg-surface-muted shrink-0">
              <ArrowLeft size={17} />
            </button>
            <div className="w-[46px] h-[46px] rounded-xl grid place-items-center shrink-0 bg-soft text-primary">
              <FileText size={22} />
            </div>
            <div className="min-w-0">
              <div className="text-xl font-extrabold -tracking-[0.3px] flex items-center gap-2.5 flex-wrap">
                {docLabel} <span className="text-content-muted">{invoice.invoice_number}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[invoice.status] ?? 'bg-surface-muted text-content-muted'}`}>
                  {invoice.status_display}
                </span>
              </div>
              <p className="text-content-muted text-[13px] mt-0.5 truncate">{invoice.supplier_name}</p>
            </div>
            <div className="ml-auto">
              <MoneyTotals stats={stats} />
            </div>
          </div>

          <div className="flex gap-2 mt-3.5 flex-wrap">
            {canApproveNow && (
              <HeaderBtn primary icon={<BadgeCheck size={15} />} label="Aprobar" onClick={() => setApproveOpen(true)} />
            )}
            {canEditIntl && (
              <HeaderBtn icon={<FilePenLine size={15} />} label="Editar" onClick={() => setEditIntlOpen(true)} />
            )}
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && canPay && (
              <HeaderBtn primary={!canApproveNow} icon={<Plus size={15} />} label="Registrar pago" onClick={() => setPayOpen(true)} />
            )}
            {['registered', 'approved'].includes(invoice.status) && canApprove && (
              <HeaderBtn icon={<XCircle size={15} />} label="Anular" onClick={requestCancel} />
            )}
            {invoice.status !== 'paid' && canDelete && (
              <HeaderBtn icon={<Trash2 size={15} />} label="Eliminar" onClick={requestDelete} />
            )}
            <OriginalPdfAction invoiceId={invoice.id} />
            <HeaderBtn icon={<Download size={15} />} label="PDF Clobi" onClick={() => openBlob(`/purchases/invoices/${invoice.id}/pdf/`)} />
            <HeaderBtn icon={<LinkIcon size={15} />} label="Copiar enlace" onClick={copyLink} />
          </div>
        </div>
      }
      chips={<InvoiceChips invoice={invoice} locale={locale} />}
      center={<CenterPane invoice={invoice} view={view} setView={setView} fmt={fmt} locale={locale} />}
      panel={
        <div className="grid grid-cols-[1fr_48px] gap-2.5 items-start">
          <div className="bg-surface border border-divider rounded-xl shadow-sm min-h-[200px] min-w-0">
            <div className="sticky top-[var(--head-h,130px)] z-[6] flex items-center gap-2 px-4 py-3 border-b border-divider font-extrabold text-sm bg-surface rounded-t-xl">
              {tabs.find((t) => t.key === activeTab)?.title}
            </div>
            <div className="p-4">
              <PanelContent invoice={invoice} activeTab={activeTab} fmt={fmt} locale={locale} navigate={navigate} actions={tabActions} />
            </div>
          </div>
          <div className="sticky top-[var(--head-h,130px)]">
            <RelatedTabsRail tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
          </div>
        </div>
      }
    />

      <ApproveWithAccountModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        isPending={approve.isPending}
        title="Aprobar factura"
        description="Al aprobar se genera un comprobante contable en borrador. Por defecto se usa la cuenta del producto / proveedor / configuración. Se puede elegir otra cuenta de gasto si hace falta."
        confirmLabel="Aprobar"
        onConfirm={handleApprove}
      />
      {payOpen && (
        <SupplierPaymentModal
          preselectedInvoice={{ ...invoice, outstanding: invoice.outstanding ?? invoice.total } as unknown as PayableInvoice}
          onSave={handleSavePayment}
          onClose={() => setPayOpen(false)}
          isPending={createPayment.isPending}
          error={undefined}
        />
      )}
      {editIntlOpen && (
        <EditIntlInvoiceModal
          invoice={invoice}
          onSave={handleSaveIntl}
          onClose={() => setEditIntlOpen(false)}
          isPending={updateInvoice.isPending}
          error={null}
        />
      )}
      <ConfirmModal {...cancelModalProps} />
      <ConfirmModal {...deleteModalProps} />
      <ConfirmModal {...acuseModalProps} />
    </>
  )
}

// ─── Chips de estado ─────────────────────────────────────────────────────────────

function InvoiceChips({ invoice, locale }: { invoice: SupplierInvoice; locale: string }) {
  const d = invoice.days_until_deadline
  const acuseVal = typeof d === 'number'
    ? (d < 0 ? `Vencido (${Math.abs(d)}d)` : `Vence en ${d}d`)
    : '—'
  const cards: Array<{ lbl: string; val: string }> = [
    { lbl: 'Vence', val: invoice.due_date ? fmtDate(invoice.due_date, locale) : '—' },
    { lbl: 'Origen', val: ORIGIN_LABELS[invoice.source] ?? invoice.source },
    { lbl: 'RCV', val: invoice.rcv_estado || '—' },
    { lbl: 'Acuse SII', val: acuseVal },
    { lbl: 'Contabilidad', val: invoice.accounting_status === 'accounted' ? 'Contabilizada' : 'Pendiente' },
    { lbl: 'Pago', val: Number(invoice.outstanding ?? 0) > 0 ? 'Pendiente' : 'Pagada' },
  ]
  return (
    <div className="grid grid-cols-6 gap-2.5 max-[860px]:grid-cols-3 max-[600px]:grid-cols-2">
      {cards.map((c) => (
        <div key={c.lbl} className="bg-surface border border-divider rounded-xl px-3 py-2.5 shadow-sm text-left">
          <div className="text-[10.5px] uppercase tracking-wide text-content-subtle">{c.lbl}</div>
          <div className="text-[13px] font-bold mt-1 truncate" title={c.val}>{c.val}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Rail de facturas ────────────────────────────────────────────────────────────

function DocsRail({
  invoices, total, activeId, locale, fmt, loading, onSelect, onLoadMore, onBack,
}: {
  invoices: SupplierInvoiceListItem[]; total: number; activeId: string; locale: string
  fmt: (v: string | number) => string; loading: boolean
  onSelect: (id: string) => void; onLoadMore: () => void; onBack: () => void
}) {
  const canLoadMore = invoices.length < total
  const activeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => { activeRef.current?.scrollIntoView({ block: 'nearest' }) }, [activeId, invoices])
  return (
    <>
      <button
        onClick={onBack}
        title="Volver al listado de facturas"
        className="flex items-center gap-2 px-3.5 py-3 border-b border-divider font-bold text-[13px] w-full text-left text-content-muted hover:bg-surface-muted hover:text-content"
      >
        <ChevronLeft size={16} />
        <span className="flex items-center gap-2"><FileText size={15} /> Facturas</span>
      </button>
      <div className="overflow-y-auto scrollbar-slim p-2 flex-1">
        {invoices.map((inv) => {
          const active = inv.id === activeId
          const label = (inv.document_type_sii && DTE_LABELS[inv.document_type_sii]) || 'Factura'
          return (
            <button
              key={inv.id}
              ref={active ? activeRef : undefined}
              onClick={() => onSelect(inv.id)}
              className={`block w-full text-left rounded-xl border p-2.5 mb-1.5 ${active ? 'bg-primary/10 border-primary' : 'border-transparent hover:bg-surface-muted'}`}
            >
              <div className="flex justify-between gap-2 text-[12.5px]">
                <span className="font-bold truncate">{label} {inv.invoice_number}</span>
                <span className="text-content-subtle shrink-0">{inv.issue_date ? fmtDate(inv.issue_date, locale) : '—'}</span>
              </div>
              <div className="text-content-muted text-[12px] my-0.5 truncate">{inv.supplier_name}</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-bold tabular-nums">{fmt(inv.total)}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${STATUS_CLS[inv.status] ?? 'bg-surface-muted text-content-muted'}`}>
                  {inv.status_display}
                </span>
              </div>
            </button>
          )
        })}
        {canLoadMore && (
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="block w-full text-center rounded-xl border border-divider px-3 py-2 mt-1 text-[12px] font-semibold text-content-muted hover:bg-surface-muted hover:text-content disabled:opacity-60"
          >
            {loading ? 'Cargando…' : `Cargar más facturas (${invoices.length} de ${total})`}
          </button>
        )}
      </div>
    </>
  )
}

// ─── Centro: Detalle / PDF Clobi / XML ────────────────────────────────────────────

function CenterPane({
  invoice, view, setView, fmt, locale,
}: { invoice: SupplierInvoice; view: CenterView; setView: (v: CenterView) => void; fmt: (v: string | number) => string; locale: string }) {
  const views: Array<{ k: CenterView; label: string; icon: React.ReactNode; disabled?: boolean }> = [
    { k: 'detail', label: 'Detalle', icon: <FilePenLine size={14} /> },
    { k: 'pdf', label: 'PDF Clobi', icon: <Download size={14} /> },
    { k: 'xml', label: 'XML', icon: <FileCode2 size={14} />, disabled: !invoice.has_xml },
  ]
  return (
    <div className="bg-surface border border-divider rounded-xl shadow-sm flex flex-col">
      <div className="sticky top-[var(--head-h,130px)] z-[6] flex items-center gap-2.5 px-3 py-2.5 border-b border-divider bg-surface rounded-t-xl">
        <div className="inline-flex bg-soft rounded-lg p-0.5">
          {views.map((v) => (
            <button key={v.k} disabled={v.disabled} onClick={() => setView(v.k)}
              className={[
                'px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold inline-flex gap-1.5 items-center disabled:opacity-40',
                view === v.k ? 'bg-surface text-content shadow-sm' : 'text-content-muted',
              ].join(' ')}>
              {v.icon}{v.label}
            </button>
          ))}
        </div>
      </div>
      <div className={view === 'detail' ? '' : 'p-[18px]'}>
        {view === 'detail' && <DetailView invoice={invoice} fmt={fmt} locale={locale} />}
        {view === 'pdf' && <BlobViewer url={`/purchases/invoices/${invoice.id}/pdf/`} kind="pdf" />}
        {view === 'xml' && <BlobViewer url={`/purchases/invoices/${invoice.id}/xml/`} kind="xml" />}
      </div>
    </div>
  )
}

function DetailView({ invoice, fmt, locale }: { invoice: SupplierInvoice; fmt: (v: string | number) => string; locale: string }) {
  const tax = useTenantTax()
  const docLabel = invoice.document_type_sii ? (DTE_LABELS[invoice.document_type_sii] ?? `DTE ${invoice.document_type_sii}`) : 'Factura'
  const fields: Array<[string, React.ReactNode]> = [
    ['Proveedor', invoice.supplier_name],
    ['Tipo de documento', docLabel],
    ['N° de factura', invoice.invoice_number],
    ['Emisión', invoice.issue_date ? fmtDate(invoice.issue_date, locale) : '—'],
    ['Vencimiento', invoice.due_date ? fmtDate(invoice.due_date, locale) : '—'],
    ['Origen del documento', invoice.source || '—'],
  ]
  return (
    <div className="p-4">
      {/* Encabezado del documento */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5 mb-5 pb-4 border-b border-divider">
        {fields.map(([l, v]) => (
          <div key={l} className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-content-subtle">{l}</div>
            <div className="text-sm text-content break-words">{v || '—'}</div>
          </div>
        ))}
      </div>

      {/* Líneas — mismo diseño que el detalle de ventas */}
      <DataTable
        columns={[
          { key: 'sku', header: 'SKU', render: (ln: POLine) => <span className="font-mono text-xs text-content-muted">{ln.sku || '—'}</span> },
          { key: 'name', header: 'Detalle', render: (ln: POLine) => <span className="font-medium">{ln.name || '—'}</span> },
          { key: 'quantity', header: 'Cant.', numeric: true },
          {
            key: 'unit_price', header: 'P. Unit.', numeric: true,
            render: (ln: POLine) => {
              // Con moneda extranjera se muestran las dos: el precio original
              // arriba y su equivalente convertido abajo.
              const isFx = !!ln.currency_original && ln.currency_original !== 'CLP' && !!ln.unit_price_original
              return isFx ? (
                <div className="space-y-0.5">
                  <div className="text-[11px] font-bold text-primary">{ln.currency_original} {ln.unit_price_original}</div>
                  <div>{fmt(ln.unit_price)}</div>
                </div>
              ) : fmt(ln.unit_price)
            },
          },
          {
            key: 'subtotal', header: 'Subtotal', numeric: true,
            render: (ln: POLine) => (
              <span className="font-semibold">
                {fmt(Number(ln.subtotal) || (Number(ln.quantity || 0) * Number(ln.unit_price || 0)))}
              </span>
            ),
          },
        ]}
        rows={invoice.lines ?? []}
        rowKey={(_, i) => i}
        minWidth={620}
        caption="Líneas de la factura"
      />
      <div className="mt-4 flex flex-col items-end gap-1 text-sm">
        <div className="flex gap-8"><span className="text-content-subtle">Neto</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.subtotal)}</span></div>
        <div className="flex gap-8"><span className="text-content-subtle">{tax.name}</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.tax_amount)}</span></div>
        {/* Montos especiales del XML (no facturable / período / exento): se
            muestran cuando el documento los trae —típico de corredoras y
            servicios periódicos donde Neto y Total vienen en 0—. */}
        {invoice.sii_montos?.exento && (
          <div className="flex gap-8"><span className="text-content-subtle">Exento</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.sii_montos.exento)}</span></div>
        )}
        {invoice.sii_montos?.monto_nf && (
          <div className="flex gap-8"><span className="text-warning">No facturable</span><span className="tabular-nums text-warning font-semibold w-32 text-right">{fmt(invoice.sii_montos.monto_nf)}</span></div>
        )}
        {invoice.sii_montos?.monto_periodo && (
          <div className="flex gap-8"><span className="text-warning">Monto período</span><span className="tabular-nums text-warning font-semibold w-32 text-right">{fmt(invoice.sii_montos.monto_periodo)}</span></div>
        )}
        {invoice.sii_montos?.saldo_anterior && (
          <div className="flex gap-8"><span className="text-content-subtle">Saldo anterior</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.sii_montos.saldo_anterior)}</span></div>
        )}
        <div className="flex gap-8 font-bold"><span className="text-content">Total</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.total)}</span></div>
        {invoice.sii_montos?.vlr_pagar && (
          <div className="flex gap-8"><span className="text-content-subtle">Valor a pagar</span><span className="tabular-nums text-content w-32 text-right">{fmt(invoice.sii_montos.vlr_pagar)}</span></div>
        )}
      </div>

      <RcvSection invoice={invoice} locale={locale} />

      <NotesSection key={invoice.id} invoiceId={invoice.id} notes={invoice.notes ?? ''} />
    </div>
  )
}

// Campo etiqueta/valor con el estilo del Detalle (label en mayúsculas + valor).
function DField({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</div>
      <div className="text-sm text-content break-words">{value || '—'}</div>
    </div>
  )
}

const detailSecHeadCls = 'text-[13px] font-extrabold uppercase tracking-wide text-content-subtle border-b border-divider pb-2 mb-3'

/**
 * Datos RCV (SII) — paridad con el modal de detalle: estado RCV, fechas de
 * recepción/acuse/reclamo, deadline y plazo restante. Solo se muestra si la
 * factura tiene datos del RCV (no aparece en facturas manuales/intl).
 */
function RcvSection({ invoice, locale }: { invoice: SupplierInvoice; locale: string }) {
  if (!invoice.rcv_fecha_recepcion && !invoice.rcv_estado) return null
  const d = invoice.days_until_deadline
  return (
    <div className="mt-6">
      <p className={detailSecHeadCls}>Datos RCV (SII)</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5">
        <DField label="Estado RCV" value={invoice.rcv_estado} />
        <DField label="Recepción SII" value={invoice.rcv_fecha_recepcion ? fmtDate(invoice.rcv_fecha_recepcion, locale) : undefined} />
        <DField label="Fecha acuse" value={invoice.rcv_fecha_acuse ? fmtDate(invoice.rcv_fecha_acuse, locale) : undefined} />
        <DField label="Fecha reclamo" value={invoice.rcv_fecha_reclamo ? fmtDate(invoice.rcv_fecha_reclamo, locale) : undefined} />
        <DField label="Deadline acuse/reclamo" value={invoice.rcv_deadline ? fmtDate(invoice.rcv_deadline, locale) : undefined} />
        {d != null && (
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-content-subtle">Plazo restante</div>
            <div className={`text-sm font-medium inline-flex items-center gap-1 ${d < 0 ? 'text-danger-content-soft' : d <= 2 ? 'text-orange-600 dark:text-orange-300' : 'text-primary'}`}>
              <Clock size={13} />{d < 0 ? `Vencido (${Math.abs(d)} días)` : `${d} días`}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Acción de cabecera para el PDF original del proveedor: si la factura ya lo
 * tiene, lo abre; si no, permite subirlo (intl cargadas por Excel, RCV sin XML).
 * Vive en el header junto al resto de acciones; la gestión completa
 * (reemplazar / quitar / ver todos los adjuntos) sigue en el tab "Adjuntos".
 */
function OriginalPdfAction({ invoiceId }: { invoiceId: string }) {
  const { can } = usePermission()
  const canEdit = can('purchases.invoices', 'edit')
  const { data } = useSupplierInvoiceAttachments(invoiceId)
  const upload = useUploadSupplierInvoiceOriginalPdf(invoiceId)
  const fileRef = useRef<HTMLInputElement>(null)
  const toast = useToast()
  const original = (data?.attachments || []).find((a) => a.kind === 'original_pdf')

  if (original) {
    return (
      <HeaderBtn icon={<FileText size={15} />} label="PDF original"
        onClick={() => window.open(original.download_url, '_blank', 'noopener')} />
    )
  }
  if (!canEdit) return null
  return (
    <>
      <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) upload.mutate(f, { onError: () => toast.error('No se pudo subir el archivo') })
          if (fileRef.current) fileRef.current.value = ''
        }} />
      <HeaderBtn
        icon={upload.isPending ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
        label="Subir PDF original" onClick={() => fileRef.current?.click()} disabled={upload.isPending} />
    </>
  )
}

function NotesSection({ invoiceId, notes }: { invoiceId: string; notes: string }) {
  const [editing, setEditing] = useState(false)
  const [draftNotes, setDraftNotes] = useState(notes)
  const update = useUpdateInvoiceNotes(invoiceId)
  const toast = useToast()
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between border-b border-divider pb-2 mb-3">
        <p className="text-[13px] font-extrabold uppercase tracking-wide text-content-subtle">Notas y observaciones</p>
        {!editing && (
          <button onClick={() => { setDraftNotes(notes); setEditing(true) }}
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-semibold">
            <Pencil size={12} />Editar
          </button>
        )}
      </div>
      {!editing ? (
        notes
          ? <p className="text-[13px] text-content-muted whitespace-pre-wrap">{notes}</p>
          : <p className="text-xs text-content-subtle">Sin notas.</p>
      ) : (
        <div className="space-y-3">
          <div>
            <label htmlFor="invoice360page-observaciones" className="text-[11.5px] font-semibold text-content-muted">Observaciones</label>
            <textarea id="invoice360page-observaciones"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 text-[13px] border border-divider rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Notas u observaciones internas…"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-divider rounded-lg text-content-muted hover:bg-soft">Cancelar</button>
            <button
              onClick={() => update.mutate(draftNotes, {
                onSuccess: () => { setEditing(false); toast.success('Notas guardadas') },
                onError: () => toast.error('No se pudieron guardar las notas'),
              })}
              disabled={update.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
            >
              {update.isPending && <Loader2 size={12} className="animate-spin" />}Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function BlobViewer({ url, kind }: { url: string; kind: 'pdf' | 'xml' }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    let revoke: string | null = null
    let active = true
    setError(false); setBlobUrl(null); setText(null)
    apiClient.get(url, { responseType: 'blob' }).then(async (res) => {
      if (!active) return
      if (kind === 'pdf') {
        const u = URL.createObjectURL(res.data); revoke = u; setBlobUrl(u)
      } else {
        setText(await res.data.text())
      }
    }).catch(() => active && setError(true))
    return () => { active = false; if (revoke) URL.revokeObjectURL(revoke) }
  }, [url, kind])
  if (error) return <div className="p-8 text-center text-content-subtle text-sm">No se pudo cargar el documento.</div>
  if (kind === 'pdf') {
    return blobUrl
      ? <iframe src={blobUrl} title="PDF" className="w-full h-[70vh] border-0" />
      : <div className="p-8 text-center text-content-subtle"><Loader2 size={18} className="animate-spin inline mr-2" />Cargando PDF…</div>
  }
  return text != null
    ? <pre className="text-xs overflow-x-auto scrollbar-slim text-content whitespace-pre-wrap max-h-[70vh]">{text}</pre>
    : <div className="p-8 text-center text-content-subtle"><Loader2 size={18} className="animate-spin inline mr-2" />Cargando XML…</div>
}

// ─── Panel contextual (7 tabs) ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[13px] font-extrabold uppercase tracking-wide text-content-subtle border-b border-divider pb-2 mb-3">{title}</p>
      {children}
    </div>
  )
}

function Row({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-sm">
      <span className="text-content-subtle shrink-0">{label}</span>
      <span className={`text-right min-w-0 break-words ${tone ?? 'text-content'}`}>{value ?? '—'}</span>
    </div>
  )
}

interface TabActions {
  canAcuseSii: boolean
  canPay: boolean
  canRequestXml: boolean
  requestingXml: boolean
  onAcuseSii: (inboundDteId: string) => void
  onAcceptSii: (inboundDteId: string) => void
  onRejectSii: (inboundDteId: string) => void
  onRegisterPayment: () => void
  onRequestXml: (email: string) => void
}

function PanelContent({
  invoice, activeTab, fmt, locale, navigate, actions,
}: {
  invoice: SupplierInvoice; activeTab: string; fmt: (v: string | number) => string; locale: string
  navigate: (to: string) => void; actions: TabActions
}) {
  switch (activeTab) {
    case 'supplier': return <SupplierTab invoice={invoice} fmt={fmt} locale={locale} navigate={navigate} />
    case 'reception': return <ReceptionTab invoiceId={invoice.id} locale={locale} actions={actions} />
    case 'related': return <RelatedTab invoice={invoice} fmt={fmt} navigate={navigate} />
    case 'voucher': return <VoucherTab invoiceId={invoice.id} fmt={fmt} />
    case 'payments': return <PaymentsTab invoiceId={invoice.id} fmt={fmt} locale={locale} actions={actions} />
    case 'files': return <FilesTab invoiceId={invoice.id} locale={locale} />
    case 'history': return <HistoryTab invoice={invoice} locale={locale} />
    default: return null
  }
}

function SupplierTab({ invoice, fmt, locale, navigate }: { invoice: SupplierInvoice; fmt: (v: string | number) => string; locale: string; navigate: (to: string) => void }) {
  const { data, isLoading } = useSupplierContext(invoice.supplier, true)
  if (isLoading) return <TabLoader />
  return (
    <div className="space-y-3">
      <Section title="Proveedor">
        <Row label="Razón social" value={data?.legal_name} />
        <Row label="RUT / ID" value={data?.tax_id} />
        {data?.giro && <Row label="Giro" value={data.giro} />}
        {data?.email && <Row label="Email" value={data.email} />}
        {data?.is_international && <Row label="País" value={data.country || 'Internacional'} />}
        <Row label="Origen del documento" value={invoice.source} />
      </Section>
      <Section title="Situación financiera">
        <Row label="Por pagar" value={data ? fmt(data.financial.por_pagar) : '—'} />
        <Row label="Vencido" value={data ? fmt(data.financial.vencido) : '—'} tone="text-danger-content-soft" />
      </Section>
      {!!data?.recent_invoices?.length && (
        <Section title="Últimas facturas">
          <div className="divide-y divide-divider">
            {data.recent_invoices.map((r) => (
              <button key={r.id} onClick={() => navigate(`/purchases/invoices/${r.id}/360`)}
                className="w-full flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-soft rounded px-1">
                <span className="text-content truncate">{r.invoice_number}</span>
                <span className="text-content-muted tabular-nums">{fmt(r.total)}</span>
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

function ReceptionTab({ invoiceId, locale, actions }: { invoiceId: string; locale: string; actions: TabActions }) {
  const { data, isLoading } = useInvoiceReception(invoiceId, true)
  const [xmlEmail, setXmlEmail] = useState('')
  if (isLoading) return <TabLoader />
  const dte = data?.inbound_dte
  const acuse = data?.sii_acuse
  return (
    <div className="space-y-3">
      <Section title="Origen">
        <Row label="Fuente" value={data?.source_display} />
        {dte && <>
          <Row label="DTE recibido" value={`${dte.tipo_dte ?? ''} #${dte.folio ?? ''}`} />
          <Row label="Estado" value={dte.status_display} />
          <Row label="Forma de pago" value={dte.forma_pago_display} />
          <Row label="Recibido" value={dte.received_at ? fmtDate(dte.received_at, locale) : '—'} />
        </>}
        {!dte && <p className="text-sm text-content-subtle">Sin DTE recibido vinculado.</p>}
      </Section>
      {acuse && (
        <Section title="Acuse / Reclamo (Ley 19.983)">
          <Row label="A crédito" value={acuse.is_credit ? 'Sí' : 'No'} />
          <Row label="Estado" value={acuse.accepted ? 'Aceptado' : acuse.rejected ? 'Reclamado' : 'Pendiente'}
            tone={acuse.accepted ? 'text-success-content-soft' : acuse.rejected ? 'text-danger-content-soft' : 'text-warning-content-soft'} />
          <Row label="Registrado en SII" value={acuse.sii_event_registered ? 'Sí' : 'No'} />
          {actions.canAcuseSii && (acuse.can_acuse || acuse.can_accept || acuse.can_reject) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-divider">
              {acuse.can_acuse && (
                <button onClick={() => actions.onAcuseSii(acuse.inbound_dte_id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface transition-colors"
                  title="Acuse de recibo de mercaderías (ERM) — despeja el RCV">
                  <PackageCheck size={14} /> Acuse recibo
                </button>
              )}
              {acuse.can_accept && (
                <button onClick={() => actions.onAcceptSii(acuse.inbound_dte_id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface transition-colors"
                  title="Aceptar contenido del documento (ACD)">
                  <BadgeCheck size={14} /> Aceptar
                </button>
              )}
              {acuse.can_reject && (
                <button onClick={() => actions.onRejectSii(acuse.inbound_dte_id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface transition-colors">
                  <XCircle size={14} /> Reclamar
                </button>
              )}
            </div>
          )}
        </Section>
      )}
      {data?.sii_events && data.sii_events.length > 0 && (
        <Section title="Eventos SII (Ley 19.983)">
          <div className="space-y-2">
            {/* Log de auditoria: no alcanza con QUE evento y CUANDO. Para
                reconstruir una decision hay que ver QUIEN la tomo, QUE declaro
                (el recinto viaja al SII; las observaciones explican el motivo,
                sobre todo en un reclamo) y QUE respondio el SII. */}
            {data.sii_events.map((ev) => (
              <div key={ev.id} className="text-sm border-b border-divider last:border-0 pb-2 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className={ev.sii_ok ? 'text-success-content-soft' : 'text-warning-content-soft'}>
                      {ev.tipo}
                    </span>
                    <span className="text-content-subtle"> · {ev.tipo_display}</span>
                    {!ev.sii_ok && (
                      <span className="ml-2 text-[11px] text-warning-content-soft">
                        no registrado en el SII
                      </span>
                    )}
                  </div>
                  <span className="text-content-subtle whitespace-nowrap text-right">
                    {ev.sii_fecha_evento || (ev.registered_at ? fmtDate(ev.registered_at, locale) : '')}
                    {ev.sent_to_emisor_at && <span className="block text-[11px] text-success-content-soft">enviado al emisor</span>}
                  </span>
                </div>
                <div className="mt-0.5 space-y-0.5 text-[11px] text-content-subtle">
                  {ev.registered_by_name && <div>Por {ev.registered_by_name}</div>}
                  {ev.recinto && <div>Recinto declarado: {ev.recinto}</div>}
                  {ev.observaciones && <div className="text-content-muted">«{ev.observaciones}»</div>}
                  {ev.sii_glosa && <div>SII: {ev.sii_glosa}</div>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
      <Section title="RCV / Tributario">
        <Row label="Período" value={data?.rcv.periodo} />
        <Row label="Estado RCV" value={data?.rcv.estado} />
        {data?.rcv.deadline && <Row label="Plazo acuse" value={fmtDate(data.rcv.deadline, locale)} />}
        {data?.rcv.xml_mismatch && <Row label="Descuadre" value="XML ≠ RCV" tone="text-warning-content-soft" />}
      </Section>
      {actions.canRequestXml && (
        <Section title="Solicitar XML al proveedor">
          <p className="text-xs text-content-subtle mb-2">
            La factura se conoce por el RCV, pero falta el XML (DTE). Se le pide al proveedor que lo reenvíe al correo de intercambio.
          </p>
          <div className="flex gap-2">
            <input aria-label="Correo de destino del XML"
              type="email" value={xmlEmail} onChange={(e) => setXmlEmail(e.target.value)}
              placeholder="correo del proveedor (opcional)"
              className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button onClick={() => actions.onRequestXml(xmlEmail)} disabled={actions.requestingXml}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors shrink-0">
              <Mail size={14} /> Solicitar
            </button>
          </div>
        </Section>
      )}
    </div>
  )
}

const NC_ND_TYPES = [56, 61, 111, 112]

function RelatedTab({ invoice, fmt, navigate }: { invoice: SupplierInvoice; fmt: (v: string | number) => string; navigate: (to: string) => void }) {
  const { data, isLoading } = useInvoiceRelated(invoice.id, true)
  if (isLoading) return <TabLoader />
  return (
    <div className="space-y-3">
      <Section title="Orden de compra">
        {data?.purchase_order
          ? <Row label={data.purchase_order.order_number} value={`${data.purchase_order.status_display} · ${fmt(data.purchase_order.total)}`} />
          : <p className="text-sm text-content-subtle">Sin orden de compra de origen.</p>}
      </Section>

      {data?.is_adjustment_orphan && <RelinkOrphan invoice={invoice} fmt={fmt} />}

      <Section title="Notas de ajuste (NC/ND)">
        {data?.linked_adjustments?.length
          ? <div className="divide-y divide-divider">
              {data.linked_adjustments.map((a) => (
                <button key={a.id} onClick={() => navigate(`/purchases/invoices/${a.id}/360`)}
                  className="w-full flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-soft rounded px-1">
                  <span className="text-content">{a.type} {a.folio}</span>
                  <span className="text-content-muted tabular-nums">{fmt(a.amount)}</span>
                </button>
              ))}
            </div>
          : <p className="text-sm text-content-subtle">Sin NC/ND vinculadas.</p>}
        <div className="mt-2 pt-2 border-t border-divider">
          <Row label="Saldo" value={fmt(data?.outstanding_amount ?? '0')} />
        </div>
      </Section>
    </div>
  )
}

function RelinkOrphan({ invoice, fmt }: { invoice: SupplierInvoice; fmt: (v: string | number) => string }) {
  const qc = useQueryClient()
  const updateRefs = useUpdateInvoiceReferencias(invoice.id)
  const toast = useToast()
  const [parentId, setParentId] = useState('')
  const { data: parentList } = useSupplierInvoices({ supplier_id: invoice.supplier, page_size: 100 })
  const candidates = (parentList?.invoices ?? []).filter(
    (i) => i.id !== invoice.id && !NC_ND_TYPES.includes(i.document_type_sii ?? 0),
  )
  const linkParent = () => {
    const parent = candidates.find((c) => c.id === parentId)
    if (!parent) return
    const ref = {
      tipo_doc: String(parent.document_type_sii ?? '33'),
      folio_ref: parent.invoice_number,
      fecha_ref: parent.issue_date,
      cod_ref: invoice.referencias?.[0]?.cod_ref || '1',
      razon_ref: invoice.referencias?.[0]?.razon_ref || '',
    }
    updateRefs.mutate([ref], {
      onSuccess: () => { qc.invalidateQueries({ queryKey: ['inv360'] }); toast.success('Nota vinculada a la factura') },
      onError: () => toast.error('No se pudo vincular la nota'),
    })
  }
  return (
    <Section title="Vincular a factura">
      <p className="text-xs text-content-subtle mb-2">
        Esta nota tiene referencias que no coinciden con ninguna factura del proveedor. Selecciona la factura que ajusta.
      </p>
      <div className="flex gap-2">
        <select aria-label="Seleccionar factura" value={parentId} onChange={(e) => setParentId(e.target.value)}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="">— Seleccionar factura —</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>{c.invoice_number} · {fmt(c.total)}</option>
          ))}
        </select>
        <button onClick={linkParent} disabled={!parentId || updateRefs.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors shrink-0">
          <Link2 size={14} /> Vincular
        </button>
      </div>
    </Section>
  )
}

function VoucherTab({ invoiceId, fmt }: { invoiceId: string; fmt: (v: string | number) => string }) {
  const { data, isLoading } = useInvoiceVoucher(invoiceId, true)
  const qc = useQueryClient()
  const toast = useToast()
  const { can } = usePermission()
  const canEdit = can('accounting.vouchers', 'edit')
  const canEditApproved = can('accounting.vouchers', 'edit_approved')
  const { confirm, modalProps } = useConfirmModal()
  const [editOpen, setEditOpen] = useState(false)

  if (isLoading) return <TabLoader />
  if (!data?.voucher) return <Section title="Comprobante contable"><p className="text-sm text-content-subtle">Sin comprobante ({data?.accounting_status ?? 'pendiente'}).</p></Section>
  const v = data.voucher
  // Editar in-place: borrador con 'edit'; aprobado solo con 'edit_approved' y confirmación de riesgo.
  const canEditThis = (v.status === 'draft' && canEdit) || (v.status === 'approved' && canEditApproved)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['inv360'] })
    qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
  }
  const openEdit = () => {
    if (v.status === 'approved') {
      confirm({
        variant: 'danger',
        confirmLabel: 'Editar de todas formas',
        message:
          'Se va a EDITAR un comprobante ya APROBADO. Esto puede descuadrar la contabilidad y ' +
          'afectar balances, EERR, F22/F29 y reportes ya emitidos. El comprobante permanece ' +
          'aprobado y el cambio queda registrado en la bitácora. ¿Continuar?',
        onConfirm: () => setEditOpen(true),
      })
    } else {
      setEditOpen(true)
    }
  }
  return (
    <Section title={`Comprobante ${v.number}`}>
      <Row label="Estado" value={v.status_display} />
      <Row label="Fecha" value={v.date} />
      {canEditThis && (
        <button
          onClick={openEdit}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
        >
          <Pencil size={13} /> Editar comprobante
        </button>
      )}
      <div className="mt-3">
        <DataTable
          columns={[
            { key: 'account', header: 'Cuenta', render: ln => `${ln.account_code} ${ln.account_name}` },
            { key: 'debit', header: 'Debe', numeric: true, render: ln => (Number(ln.debit) ? fmt(ln.debit) : '') },
            { key: 'credit', header: 'Haber', numeric: true, render: ln => (Number(ln.credit) ? fmt(ln.credit) : '') },
          ]}
          rows={v.lines}
          rowKey={(_, i) => i}
          surface={false}
          minWidth={420}
          caption="Líneas del comprobante contable"
        />
      </div>
      {editOpen && (
        <VoucherForm
          voucher={{ id: v.id }}
          onClose={() => setEditOpen(false)}
          onSaved={() => { toast.success(`Comprobante ${v.number} actualizado.`); refresh() }}
        />
      )}
      <ConfirmModal {...modalProps} />
    </Section>
  )
}

function PaymentsTab({ invoiceId, fmt, locale, actions }: { invoiceId: string; fmt: (v: string | number) => string; locale: string; actions: TabActions }) {
  const { data, isLoading } = useInvoicePayments(invoiceId, true)
  if (isLoading) return <TabLoader />
  const hasSaldo = Number(data?.outstanding_amount ?? 0) > 0
  return (
    <div className="space-y-3">
      <Section title="Saldo">
        <Row label="Total" value={fmt(data?.total ?? '0')} />
        <Row label="Saldo pendiente" value={fmt(data?.outstanding_amount ?? '0')} tone={hasSaldo ? 'text-content' : 'text-success-content-soft'} />
        {hasSaldo && actions.canPay && (
          <button onClick={actions.onRegisterPayment}
            className="w-full mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-primary border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
            <Plus size={14} /> Registrar pago
          </button>
        )}
      </Section>
      <Section title="Pagos">
        {data?.payments?.length
          ? <div className="divide-y divide-divider">
              {data.payments.map((p) => (
                <div key={p.id} className="py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-content">{p.payment_date ? fmtDate(p.payment_date, locale) : '—'} · {p.payment_method_display}</span>
                    <span className="text-content tabular-nums">{formatMoneyForTenant(p.amount, p.currency || undefined)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-content-subtle">
                    <span>{p.status_display}{p.reference ? ` · ${p.reference}` : ''}</span>
                    {p.voucher_number && <span>{p.voucher_number}</span>}
                  </div>
                </div>
              ))}
            </div>
          : <p className="text-sm text-content-subtle">Sin pagos registrados.</p>}
      </Section>
    </div>
  )
}

function FilesTab({ invoiceId }: { invoiceId: string; locale: string }) {
  const { data, isLoading } = useSupplierInvoiceAttachments(invoiceId)
  const upload = useUploadSupplierInvoiceOriginalPdf(invoiceId)
  const del = useDeleteSupplierInvoiceAttachment(invoiceId)
  const fileRef = useRef<HTMLInputElement>(null)
  const toast = useToast()
  if (isLoading) return <TabLoader />
  const hasOriginal = (data?.attachments || []).some((a) => a.kind === 'original_pdf')
  return (
    <Section title="Adjuntos">
      <div className="mb-3">
        <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) upload.mutate(f, { onError: () => toast.error('No se pudo subir el archivo') })
            if (fileRef.current) fileRef.current.value = ''
          }} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-primary border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors">
          {upload.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {hasOriginal ? 'Reemplazar PDF original' : 'Subir PDF original'}
        </button>
      </div>
      {data?.attachments?.length
        ? <div className="divide-y divide-divider">
            {data.attachments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p className="text-content truncate">{a.file_name}</p>
                  <p className="text-xs text-content-subtle">{a.kind_display}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={a.download_url} target="_blank" rel="noopener noreferrer" title="Abrir"
                    className="p-1.5 rounded-lg text-content-subtle hover:text-content hover:bg-soft"><ExternalLink size={14} /></a>
                  {a.source !== 'voucher' && (
                    <button onClick={() => del.mutate(a.id)} disabled={del.isPending} title="Eliminar"
                      className="p-1.5 rounded-lg text-content-subtle hover:text-danger-content-soft hover:bg-soft"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        : <p className="text-sm text-content-subtle">Sin adjuntos.</p>}
    </Section>
  )
}

function HistoryTab({ invoice, locale }: { invoice: SupplierInvoice; locale: string }) {
  return (
    <Section title="Historial">
      <Row label="Creada" value={invoice.created_at ? fmtDate(invoice.created_at, locale) : '—'} />
      <Row label="Actualizada" value={invoice.updated_at ? fmtDate(invoice.updated_at, locale) : '—'} />
      <Row label="Origen" value={invoice.source} />
    </Section>
  )
}

function TabLoader() {
  return <div className="flex items-center justify-center py-10 text-content-subtle"><Loader2 size={18} className="animate-spin mr-2" />Cargando…</div>
}

function HeaderBtn({ label, icon, primary, onClick, disabled }: { label: string; icon: React.ReactNode; primary?: boolean; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'rounded-lg px-3.5 py-2 text-[13px] font-semibold inline-flex items-center gap-1.5 border disabled:opacity-60',
        primary
          ? 'bg-primary border-primary text-on-primary hover:bg-primary-hover'
          : 'bg-surface border-divider-strong text-content hover:bg-surface-muted',
      ].join(' ')}
    >
      {icon}{label}
    </button>
  )
}
